import i18next from 'i18next';
import axios from 'axios';
import { Linking } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { cloneDeep } from 'lodash';

import { API_URL } from '../lib/config';

import authStore from '../stores/auth';
import appStore from '../stores/app';

import { IS_WEB } from '../lib/appConstants';

export function t(...args: any) {
  return i18next.t(...args);
}

let apiInstance: any;
let numberOfRequestsPending = 0;
let still401 = 0;
export function fetch(
  url: string,
  params: Record<string, any> = {},
  method: 'get' | 'post' = 'get',
) {
  if (apiInstance) {
    if (method === 'post') return apiInstance.post(url, cloneDeep(params), { params: {} });
    return apiInstance.get(url, { params: cloneDeep(params) });
  }
  apiInstance = axios.create({
    baseURL: API_URL,
    timeout: 10000,
  });

  function stopLoading(toggleLoading = true) {
    numberOfRequestsPending -= 1;
    if (!numberOfRequestsPending && toggleLoading) appStore.toggleSmallLoading(false);
  }

  apiInstance.interceptors.request.use((request: any) => {
    numberOfRequestsPending += 1;
    appStore.toggleSmallLoading(true);
    if (authStore.isLoggedIn) {
      request.params = request.params || {};
      request.params.idToken = authStore.user.idToken;
    }

    return request;
  });

  apiInstance.interceptors.response.use(
    (response: any) => {
      still401 = 0;
      stopLoading();
      return response;
    },
    (e: any) => {
      const originalRequest = e.config;
      const is401 = e.response && e.response.status === 401 && still401 < 5;
      stopLoading(!is401);

      if (is401) {
        still401 += 1;
        return authStore.refreshIdToken().then(() => apiInstance(originalRequest));
      }

      still401 = 0;

      const errorMessages: Record<string, string> = {
        'Network Error': t('networkError'),
        'timeout of 10000ms exceeded': t('somethingWrongError'),
      };

      // The backend returns { error: <key> } on validation failures (e.g. clan
      // actions). The response has no JSON content-type, so axios may hand it back
      // as a string — parse it and surface a friendly, translated message.
      let serverError: string | undefined;
      const data = e.response && e.response.data;
      if (data) {
        let payload: any = data;
        if (typeof data === 'string') {
          try {
            payload = JSON.parse(data);
          } catch (_) {
            payload = null;
          }
        }
        if (payload && payload.error) serverError = payload.error;
      }

      // A dotted key is already fully-qualified (e.g. "hero.bossTooLowLevel");
      // a bare key is a clan validation code under clan.errors.*.
      const serverErrorText = serverError
        ? serverError.includes('.')
          ? t(serverError)
          : t(`clan.errors.${serverError}`)
        : null;

      appStore.showMessage({
        text:
          serverErrorText ||
          errorMessages[e.message] ||
          e.message ||
          t('somethingWrongError'),
        type: 'error',
      });

      appStore.toggleLoading(false);

      return Promise.reject(e);
    },
  );

  if (method === 'post') return apiInstance.post(url, params, { params: {} });
  return apiInstance.get(url, { params });
}

export function getLng(): string {
  return i18next.language || 'en';
}

export function getLngKey(): string {
  const lng = getLng();
  return `${lng === 'en' ? '' : `_${lng}`}`;
}

export function getLocalValue(obj: any, key: string = 'name'): string {
  const value = obj[`${key}${getLngKey()}`];
  return value || obj[key];
}

export async function getFromStorage(key: string): Promise<any> {
  const value = IS_WEB ? localStorage.getItem(key) : await AsyncStorage.getItem(key);
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (e) {
    return value;
  }
}

export async function setToStorage(key: string, value: any) {
  const newValue = typeof value === 'object' ? JSON.stringify(value) : value;
  IS_WEB ? localStorage.setItem(key, newValue) : await AsyncStorage.setItem(key, newValue);
}

export async function removeFromStorage(key: string) {
  IS_WEB ? localStorage.removeItem(key) : await AsyncStorage.removeItem(key);
}

export function appendStyle(style: string) {
  const styleEl = document.createElement('style');
  (styleEl as any).type = 'text/css';

  if ((styleEl as any).styleSheet) {
    (styleEl as any).styleSheet.cssText = style;
  } else {
    styleEl.appendChild(document.createTextNode(style));
  }

  document.head.appendChild(styleEl);
}

export function action(fn: Function) {
  if (appStore.smallLoading) return;
  fn();
}

export const linkTo = (link: string) => {
  if (IS_WEB) window.open(link, '_blank')?.focus();
  else Linking.openURL(link);
};
